import { useEffect, useState } from 'react'

export interface AttributionData {
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_content: string
  utm_term: string
  utm_id: string
  fbclid: string
  fbc: string
  fbp: string
  ad_id: string
  adset_id: string
  campaign_id: string
  ad_name: string
  adset_name: string
  campaign_name: string
  placement: string
  site_source_name: string
  gclid: string
  gbraid: string
  wbraid: string
  gad_source: string
  gad_campaignid: string
  srsltid: string
  msclkid: string
  ttclid: string
  ttp: string
  li_fat_id: string
  twclid: string
  landing_url: string
  referrer: string
  ip_country: string
  user_agent: string
  device_type: string
  operating_system: string
  browser: string
  session_id: string
  first_page_visited: string
}

const getCookie = (name: string): string => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift() || ''
  return ''
}

const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const parseUserAgent = (ua: string) => {
  let device_type = 'desktop'
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    device_type = 'tablet'
  } else if (
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua,
    )
  ) {
    device_type = 'mobile'
  }

  let operating_system = 'Other'
  if (/windows phone/i.test(ua)) operating_system = 'Windows Phone'
  else if (/win/i.test(ua)) operating_system = 'Windows'
  else if (/mac/i.test(ua)) operating_system = 'macOS'
  else if (/linux/i.test(ua)) operating_system = 'Linux'
  else if (/android/i.test(ua)) operating_system = 'Android'
  else if (/ipad|iphone|ipod/i.test(ua)) operating_system = 'iOS'

  let browser = 'Other'
  if (/edg/i.test(ua)) browser = 'Edge'
  else if (/chrome|crios|crmo/i.test(ua)) browser = 'Chrome'
  else if (/firefox|iceweasel|fxios/i.test(ua)) browser = 'Firefox'
  else if (/safari/i.test(ua)) browser = 'Safari'
  else if (/opera|opr/i.test(ua)) browser = 'Opera'

  return { device_type, operating_system, browser }
}

export const useAttribution = () => {
  const [attribution, setAttribution] = useState<AttributionData | null>(null)

  useEffect(() => {
    const initAttribution = async () => {
      const urlParams = new URLSearchParams(window.location.search)
      const paramsToTrack = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_content',
        'utm_term',
        'utm_id',
        'fbclid',
        'ad_id',
        'adset_id',
        'campaign_id',
        'ad_name',
        'adset_name',
        'campaign_name',
        'placement',
        'site_source_name',
        'gclid',
        'gbraid',
        'wbraid',
        'gad_source',
        'gad_campaignid',
        'srsltid',
        'msclkid',
        'ttclid',
        'li_fat_id',
        'twclid',
      ]

      const data: Record<string, string> = {}

      // 1. Read from URL or SessionStorage
      paramsToTrack.forEach((param) => {
        const urlVal = urlParams.get(param)
        if (urlVal) {
          data[param] = urlVal
          sessionStorage.setItem(`mp_attr_${param}`, urlVal)
        } else {
          data[param] = sessionStorage.getItem(`mp_attr_${param}`) || ''
        }
      })

      // 2. Cookies
      data.fbp = getCookie('_fbp')
      data.ttp = getCookie('_ttp')

      let fbc = getCookie('_fbc')
      if (!fbc && data.fbclid) {
        fbc = `fb.1.${Date.now()}.${data.fbclid}`
      }
      data.fbc = fbc

      // 3. Session & Page Info
      let sessionId = sessionStorage.getItem('mp_session_id')
      if (!sessionId) {
        sessionId = generateUUID()
        sessionStorage.setItem('mp_session_id', sessionId)
      }
      data.session_id = sessionId

      let firstLandingUrl = sessionStorage.getItem('first_landing_url')
      if (!firstLandingUrl) {
        firstLandingUrl = window.location.href
        sessionStorage.setItem('first_landing_url', firstLandingUrl)
      }
      data.landing_url = firstLandingUrl

      let firstPageVisited = sessionStorage.getItem('first_page_visited')
      if (!firstPageVisited) {
        firstPageVisited = window.location.pathname
        sessionStorage.setItem('first_page_visited', firstPageVisited)
      }
      data.first_page_visited = firstPageVisited

      data.referrer = document.referrer || ''

      // 4. User Agent Info
      const ua = navigator.userAgent
      data.user_agent = ua
      const parsedUa = parseUserAgent(ua)
      data.device_type = parsedUa.device_type
      data.operating_system = parsedUa.operating_system
      data.browser = parsedUa.browser

      // 5. IP Country (Async)
      data.ip_country = ''
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 2000)
        const res = await fetch('https://ipapi.co/country/', {
          signal: controller.signal,
        })
        clearTimeout(timeoutId)
        if (res.ok) {
          data.ip_country = await res.text()
        }
      } catch (e) {
        // Silently fail
      }

      setAttribution(data as unknown as AttributionData)
    }

    initAttribution()
  }, [])

  return attribution
}
